import { z } from 'zod';
import { customListTypes, fieldTypeSchema, nativeDateTimeFieldIds } from './shared-constants';

export const baseFilterDTOSchema = z.object({
  fieldType: fieldTypeSchema,
  viewId: z.number().int().positive(),
});

const listFilterBaseSchema = z.object({
  ...baseFilterDTOSchema.shape,
  fieldId: z.string().min(1),
  fieldType: z.enum(customListTypes),
  valueIds: z.array(z.number().int()).min(1).optional(),
  operator: z.enum(['null', 'notnull']).optional(),
});

export const listFilterDTOSchema = listFilterBaseSchema.superRefine((data, ctx) => {
  validateListFilter(data, ctx);
});

export const dateTimeFilterDTOSchema = z
  .object({
    ...baseFilterDTOSchema.shape,
    fieldId: z.enum(nativeDateTimeFieldIds),
    fieldType: z.literal('DATETIME'),
    minTimestamp: z.iso.datetime().optional(),
    maxTimestamp: z.iso.datetime().optional(),
    operator: z.enum(['eq', 'gt', 'lt', 'in', 'null', 'notnull']).optional(),
  })
  .superRefine(validateDateTimeTimestamps);

export type ListFilterDTO = z.infer<typeof listFilterDTOSchema>;
export type DateTimeFilterDTO = z.infer<typeof dateTimeFilterDTOSchema>;

export const viewFilterDTOSchema = z.discriminatedUnion('fieldType', [
  listFilterDTOSchema,
  dateTimeFilterDTOSchema,
]);

export type FilterDTO = z.infer<typeof viewFilterDTOSchema>;

export const listFilterUpsertDTOSchema = listFilterBaseSchema
  .omit({ fieldId: true, viewId: true })
  .strict()
  .superRefine((data, ctx) => {
    validateListFilter(data, ctx);
  });

export const dateTimeFilterUpsertDTOSchema = z
  .object({
    ...baseFilterDTOSchema.omit({ viewId: true }).shape,
    fieldType: z.literal('DATETIME'),
    minTimestamp: z.iso.datetime().optional(),
    maxTimestamp: z.iso.datetime().optional(),
    operator: z.enum(['eq', 'gt', 'lt', 'in', 'null', 'notnull']),
  })
  .strict()
  .superRefine(validateDateTimeTimestamps);

function validateListFilter(
  data: ListFilterDTO | z.infer<typeof listFilterUpsertDTOSchema>,
  ctx: z.RefinementCtx,
) {
  // operators and valueIds are mutually exclusive. Don't check the operator type as that's handled in the enum validation
  if (data.operator && data.valueIds && data.valueIds.length > 0) {
    ctx.addIssue({
      code: 'custom',
      message: 'Operator cannot be used with other filter values',
      path: ['operator'],
    });
    return;
  }

  // Ensure either valueIds or empty operator is provided
  if (!data.operator && (!data.valueIds || data.valueIds.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      message: 'Either valueIds or operator must be provided',
      path: ['valueIds', 'operator'],
    });
  }
}

function validateDateTimeTimestamps(
  data: DateTimeFilterDTO | z.infer<typeof dateTimeFilterUpsertDTOSchema>,
  ctx: z.RefinementCtx,
) {
  // Validate operator-specific timestamp requirements
  switch (data.operator) {
    case 'null':
    case 'notnull':
      if (data.minTimestamp || data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Null/notnull operator cannot be used with other filter values',
          path: ['operator'],
        });
      }
      break;
    case 'gt':
      if (!data.minTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'minTimestamp is required for gt (greater than) operator',
          path: ['minTimestamp'],
        });
      }
      if (data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'maxTimestamp cannot be used with gt (greater than) operator',
          path: ['maxTimestamp'],
        });
      }
      break;
    case 'lt':
      if (!data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'maxTimestamp is required for lt (less than) operator',
          path: ['maxTimestamp'],
        });
      }
      if (data.minTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'minTimestamp cannot be used with lt (less than) operator',
          path: ['minTimestamp'],
        });
      }
      break;
    case 'in':
      if (!data.minTimestamp || !data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Both minTimestamp and maxTimestamp are required for in operator',
          path: ['minTimestamp', 'maxTimestamp'],
        });
      }
      break;
    case 'eq':
      if (!data.minTimestamp || !data.maxTimestamp) {
        ctx.addIssue({
          code: 'custom',
          message: 'Both minTimestamp and maxTimestamp are required for eq operator',
          path: ['minTimestamp', 'maxTimestamp'],
        });
      }
      break;
  }
}

export const filterUpsertDTOSchema = z.discriminatedUnion('fieldType', [
  listFilterUpsertDTOSchema,
  dateTimeFilterUpsertDTOSchema,
]);

export type FilterUpsertDTO = z.infer<typeof filterUpsertDTOSchema>;
